/**
 * Polaris × Call of Duty — Full Throttle Challenge — Brand Config
 *
 * Sibling of MINELAB_RTTR_HUB_CONFIG. Collects all the Full Throttle-specific
 * bindings (slug, loader component, lazy hub component, font preload list)
 * into one object that the registry imports.
 *
 * Adding this campaign requires the THREE coordinated edits documented in
 * web/src/customHubs/registry.ts (this brand config + registry import +
 * web/index.html inline CUSTOM_HUBS array entry).
 */

import React, {lazy} from "react";
import type {CustomHubProps} from "@/pages/hub/custom/types";
import type {CustomHubBrandConfig} from "../registry";
import {FullThrottleLoadingFallback} from "@/pages/hub/custom/polaris-cod-fullthrottle/components/FullThrottleLoadingFallback";

/*
 * Hitmarker Condensed weights self-hosted under /fonts/Hitmarker/Display/Condensed/WOFF/.
 * Order mirrors typical use frequency (body weight first, display weight
 * last) — earlier preloads start downloading sooner under HTTP/2 multiplexing.
 *
 * Keep this in sync with the @font-face declarations in
 * FullThrottleHub.scss (lands in Phase 4). If the SCSS adds a new weight or
 * renames a file, update both — there's no compile-time link between the
 * two today.
 */
const FULLTHROTTLE_FONT_PRELOAD_HREFS = [
    "/fonts/Hitmarker/Display/Condensed/WOFF/HitmarkerCondensed-Regular.woff2",
    "/fonts/Hitmarker/Display/Condensed/WOFF/HitmarkerCondensed-Medium.woff2",
    "/fonts/Hitmarker/Display/Condensed/WOFF/HitmarkerCondensed-Bold.woff2",
    "/fonts/Hitmarker/Display/Condensed/WOFF/HitmarkerCondensed-Black.woff2",
] as const;

/*
 * Lazy import of the hub component. Mirrors the existing definition in
 * HubPageRouter.tsx PRODUCTION_HUBS so the chunk-graph shape stays
 * consistent across the partner-isolated hubs.
 */
const FullThrottleHubLazy: React.LazyExoticComponent<React.FC<CustomHubProps>> = lazy(() =>
    import("@/pages/hub/custom/polaris-cod-fullthrottle/FullThrottleHub"));

export const POLARIS_FULLTHROTTLE_HUB_CONFIG: CustomHubBrandConfig = {
    slug: "polaris-cod-fullthrottle",
    loaderComponent: FullThrottleLoadingFallback,
    lazyComponent: FullThrottleHubLazy,
    fontPreloadHrefs: FULLTHROTTLE_FONT_PRELOAD_HREFS,
    /*
     * Hitmarker Condensed ships as .woff2 (Web Open Font Format 2). Preloads
     * MUST set type="font/woff2" or Chromium-based browsers reject the
     * preload. Different MIME than Minelab's Industry (.otf) — keep these
     * matching the @font-face source declarations.
     */
    fontPreloadType: "font/woff2",

    /* Mounts PolarisPrizeNotifier inside FullThrottleHub. */
    rendersOwnRewardNotifications: true,
};
